import React, { useEffect, useRef, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { Files } from 'lucide-react';

import {
	type Invoice,
	type PromoCodeValidationRequest,
	type PurchaseCreditsRequest,
	type SetupMeteredRequest,
	useGetBillingConfigQuery,
	useGetInvoiceDetailsQuery,
	useGetInvoicesQuery,
	useGetPaymentStatusQuery,
	usePurchaseCreditsMutation,
	useSetupMeteredMutation,
	useValidatePromoCodeMutation,
} from '../api';
import {
	Button,
	Card,
	CardBody,
	Input,
	PageContent,
	PageHeader,
	SearchInput,
	StatusPill,
} from '../components/common';
import Loader from '../components/Loader';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../hooks';
import { useOnboarding } from '../hooks/useOnboarding';
import { useAuth } from '../providers/AppAuthProvider';

interface SubscriptionData {
	creditsRemaining: number;
	canUpload: boolean;
	subscriptionStatus: 'active' | 'inactive' | 'trial';
	planType: 'basic' | 'premium' | 'enterprise';
	billingModel: 'credit_based' | 'metered_usage';
	usageThisPeriod?: number;
	usageLimit?: number;
}

const SubscriptionPage: React.FC = (props: any) => {
	const { onboardingMode } = props;
	const { user } = useAuth();
	const location = useLocation();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const { subscriptionStatus, refetch: refetchSubscription } =
		useSubscription();
	const {
		completeStepById,
		isStepCompleted,
		startedAt,
		currentStepId,
		checkAndStartOnboarding,
		goToStep,
	} = useOnboarding();

	// RTK Query hooks
	const {
		data: invoicesData,
		isLoading: invoicesLoading,
		refetch: refetchInvoices,
	} = useGetInvoicesQuery();
	const { data: billingConfigData, isLoading: billingConfigLoading } =
		useGetBillingConfigQuery();
	const [validatePromoCodeMutation] = useValidatePromoCodeMutation();
	const [purchaseCreditsMutation] = usePurchaseCreditsMutation();
	const [setupMeteredMutation] = useSetupMeteredMutation();
	// We'll use skip pattern for lazy queries
	const [paymentSessionId, setPaymentSessionId] = useState<string | null>(null);
	const [invoiceIdToFetch, setInvoiceIdToFetch] = useState<string | null>(null);

	// Lazy query hooks
	const { data: paymentStatusData } = useGetPaymentStatusQuery(
		paymentSessionId!,
		{
			skip: !paymentSessionId,
		},
	);

	const { data: invoiceDetailsData } = useGetInvoiceDetailsQuery(
		invoiceIdToFetch!,
		{
			skip: !invoiceIdToFetch,
		},
	);

	const [subscription, setSubscription] = useState<SubscriptionData>({
		creditsRemaining: 0,
		canUpload: false,
		subscriptionStatus: 'inactive',
		planType: 'basic',
		billingModel: 'credit_based',
	});

	const hasProcessedUrlParams = useRef(false);
	const processedSessionIds = useRef(new Set<string>());
	const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
	const [loading, setLoading] = useState(false);
	const [quantity, setQuantity] = useState('');
	const [processingPayment, setProcessingPayment] = useState(false);
	const [showInvoiceModal, setShowInvoiceModal] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [showPromoCode, setShowPromoCode] = useState(false);
	const [promoCode, setPromoCode] = useState('');
	const [appliedPromoCode, setAppliedPromoCode] = useState('');
	const [promoValidation, setPromoValidation] = useState<{
		valid: boolean;
		message: string;
		discount?: any;
	} | null>(null);
	const [validatingPromo, setValidatingPromo] = useState(false);
	const [billingModel, setBillingModel] = useState<
		'credit_based' | 'metered_usage'
	>('credit_based');
	const [showBillingToggle, setShowBillingToggle] = useState(false);

	// Update subscription data when hook data changes
	useEffect(() => {
		if (subscriptionStatus) {
			setSubscription({
				creditsRemaining: subscriptionStatus.credits_remaining || 0,
				canUpload: subscriptionStatus.can_upload || false,
				subscriptionStatus: (subscriptionStatus.has_subscription
					? subscriptionStatus.subscription_status || 'active'
					: 'inactive') as 'active' | 'inactive' | 'trial',
				planType: 'basic', // Default for now
				billingModel: (subscriptionStatus.billing_model || 'credit_based') as
					| 'credit_based'
					| 'metered_usage',
				usageThisPeriod: subscriptionStatus.usage_this_period || undefined,
				usageLimit: subscriptionStatus.usage_limit || undefined,
			});
		}
	}, [subscriptionStatus]);

	useEffect(() => {
		if (!hasProcessedUrlParams.current) {
			hasProcessedUrlParams.current = true;
			handleUrlParams();
		}
	}, []);

	// Update billing config when data changes
	useEffect(() => {
		if (billingConfigData) {
			setShowBillingToggle(billingConfigData.show_billing_toggle || false);
			if (billingConfigData.default_billing_model) {
				setBillingModel(billingConfigData.default_billing_model);
			}
		}
	}, [billingConfigData]);

	// Handle payment status response
	useEffect(() => {
		if (
			paymentStatusData &&
			paymentSessionId &&
			!processedSessionIds.current.has(paymentSessionId)
		) {
			console.log('Payment status response:', paymentStatusData);
			processedSessionIds.current.add(paymentSessionId);

			if (paymentStatusData.payment_status === 'paid') {
				const successMessage = paymentStatusData.credits_added
					? `Payment successful! ${paymentStatusData.credits_added} credits have been added to your account.`
					: 'Your subscription has been updated.';
				showToast('Payment Successful', successMessage, 'success');

				// Auto-complete the purchase step when payment is successful
				const isOnOnboardingRoute =
					window.location.pathname.includes('/onboarding');

				if (isOnOnboardingRoute && !isStepCompleted(2)) {
					if (!startedAt) {
						checkAndStartOnboarding();
					}
					if (currentStepId !== 2) {
						goToStep(2);
					}
					setTimeout(() => {
						completeStepById(2);
						navigate('/onboarding');
					}, 1000);
				}

				const url = new URL(window.location.href);
				url.searchParams.delete('session_id');
				url.searchParams.delete('success');
				window.history.replaceState({}, '', url.toString());
			} else {
				showToast(
					'Payment Processing',
					'Payment is still being processed. Please check back in a few minutes.',
					'info',
				);
			}

			// Clear the session ID to prevent re-triggering
			setPaymentSessionId(null);
		}
	}, [paymentStatusData, paymentSessionId]);

	// Handle invoice details response
	useEffect(() => {
		if (invoiceDetailsData) {
			setSelectedInvoice(invoiceDetailsData.invoice);
			setShowInvoiceModal(true);
			// Clear the invoice ID to prevent re-triggering
			setInvoiceIdToFetch(null);
		}
	}, [invoiceDetailsData]);

	// Handle URL parameters for payment success/failure feedback
	const handleUrlParams = () => {
		const searchParams = new URLSearchParams(location.search);
		const success = searchParams.get('success');
		const canceled = searchParams.get('canceled');
		const error = searchParams.get('error');
		const sessionId = searchParams.get('session_id');

		if (success === 'true') {
			if (sessionId && !processedSessionIds.current.has(sessionId)) {
				checkPaymentStatus(sessionId);
				setTimeout(() => {
					if (refetchSubscription) {
						refetchSubscription().catch((error) => {
							console.error('Failed to refetch subscription:', error);
						});
					}
					refetchInvoices();
				}, 2000);
			}
		} else if (canceled === 'true') {
			const cancelMessage = 'Payment was canceled. No charges were made.';
			console.log(cancelMessage);
			showToast('Payment Canceled', cancelMessage, 'info');
		} else if (error) {
			const errorMessage = `Payment failed: ${error}`;
			console.error(errorMessage);
			showToast('Payment Failed', errorMessage, 'error');
		}
	};

	// Check payment status using session ID
	const checkPaymentStatus = (sessionId: string) => {
		setPaymentSessionId(sessionId);
	};

	const fetchInvoiceDetails = (invoiceId: string) => {
		setInvoiceIdToFetch(invoiceId);
	};

	const validatePromoCode = async () => {
		if (!promoCode.trim()) {
			setPromoValidation({
				valid: false,
				message: 'Please enter a promo code',
			});
			return;
		}

		setValidatingPromo(true);
		setPromoValidation(null);

		try {
			const request: PromoCodeValidationRequest = {
				promo_code: promoCode,
			};

			const data = await validatePromoCodeMutation(request).unwrap();

			if (data.valid) {
				setAppliedPromoCode(promoCode);
				setPromoValidation({
					valid: true,
					message: data.message,
					discount: data.promo_code,
				});
			} else {
				setPromoValidation({
					valid: false,
					message: data.message || 'Invalid promo code',
				});
			}
		} catch (error: any) {
			console.error('Promo validation error:', error);
			setPromoValidation({
				valid: false,
				message:
					error?.data?.message ||
					'Failed to validate promo code. Please try again.',
			});
		} finally {
			setValidatingPromo(false);
		}
	};

	const clearPromoCode = () => {
		setPromoCode('');
		setAppliedPromoCode('');
		setPromoValidation(null);
	};

	const handleSetupMetered = async () => {
		setProcessingPayment(true);
		try {
			const request: SetupMeteredRequest = {
				promo_code: appliedPromoCode || undefined,
			};

			const data = await setupMeteredMutation(request).unwrap();

			if (data.success && data.checkout_url) {
				window.location.href = data.checkout_url;
			} else {
				showToast(
					'Metered Billing Error',
					'Failed to setup metered billing. Please try again.',
					'error',
				);
			}
		} catch (error: any) {
			console.error('Metered setup error:', error);
			const errorData = error?.data;
			if (errorData?.error_code === 'METERED_BILLING_NOT_CONFIGURED') {
				showToast(
					'Metered Billing Not Available',
					'Metered billing is not yet configured. Please contact support or use credit-based billing for now.',
					'warning',
				);
			} else if (errorData?.error_code === 'METERED_BILLING_NOT_AVAILABLE') {
				showToast(
					'Metered Billing Disabled',
					'Metered billing is currently disabled. Please use credit-based billing or contact support.',
					'warning',
				);
			} else {
				showToast(
					'Metered Billing Error',
					errorData?.error ||
						errorData?.message ||
						'Failed to setup metered billing',
					'error',
				);
			}
		} finally {
			setProcessingPayment(false);
		}
	};

	const switchBillingModel = async (
		newModel: 'credit_based' | 'metered_usage',
	) => {
		try {
			setBillingModel(newModel);
		} catch (error) {
			console.error('Failed to switch billing model:', error);
		}
	};

	const handlePurchaseCredits = async () => {
		const numQuantity = parseInt(quantity);
		if (!numQuantity || numQuantity < 1) return;

		setProcessingPayment(true);
		try {
			const request: PurchaseCreditsRequest = {
				quantity: numQuantity,
				description: `Purchase ${numQuantity} statement analysis credit(s)`,
				promo_code: appliedPromoCode || undefined,
			};

			if (onboardingMode) {
				request.user_type = 'onboarding';
			}

			const data = await purchaseCreditsMutation(request).unwrap();

			if (data.success && data.checkout_url) {
				window.location.href = data.checkout_url;
			} else {
				console.error('Missing checkout URL in response:', data);
				showToast(
					'Payment Error',
					'Failed to get checkout URL. Please try again.',
					'error',
				);
			}
		} catch (error: any) {
			console.error('Payment error:', error);
			showToast(
				'Payment Error',
				error?.data?.error ||
					error?.data?.message ||
					'Payment processing failed. Please try again.',
				'error',
			);
		} finally {
			setProcessingPayment(false);
		}
	};

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
		}).format(amount);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
		});
	};

	// Check if user is in onboarding mode
	const isOnboarding =
		new URLSearchParams(location.search).get('onboarding') === 'true' ||
		location.pathname.startsWith('/onboarding');

	// Filter invoices based on search term
	const filteredInvoices = (invoicesData?.invoices || []).filter((invoice) => {
		if (!searchTerm) return true;
		const searchLower = searchTerm.toLowerCase();
		const dateStr = new Date(invoice.created * 1000)
			.toLocaleDateString('en-US', {
				month: 'short',
				day: '2-digit',
				year: 'numeric',
			})
			.toLowerCase();
		const quantityStr = (invoice.lines?.[0]?.quantity || 0).toString();
		const amountStr = formatCurrency(invoice.amount_paid).toLowerCase();
		const statusStr = invoice.status.toLowerCase();

		return (
			dateStr.includes(searchLower) ||
			quantityStr.includes(searchLower) ||
			amountStr.includes(searchLower) ||
			statusStr.includes(searchLower)
		);
	});

	// Handle onboarding continue
	const handleOnboardingContinue = () => {
		navigate('/onboarding');
	};

	if (invoicesLoading || billingConfigLoading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
				<Loader size="lg" text="Loading subscription data..." />
			</div>
		);
	}

	return (
		<div className="flex flex-1 flex-col">
			<PageHeader title="Subscription & Invoices" variant="title" />

			<PageContent className="flex flex-1 flex-col mx-auto">
				{/* Billing Model Toggle Section */}
				{showBillingToggle && (
					<Card className="w-full mb-8">
						<CardBody className="p-6">
							<div className="flex items-center justify-between">
								<div>
									<h3 className="text-lg font-semibold text-gray-900">
										Billing Model
									</h3>
									<p className="text-sm text-gray-600 mt-1">
										Choose how you want to be billed for statement analysis
									</p>
								</div>
								<div className="flex items-center space-x-4">
									<div className="flex items-center space-x-2">
										<input
											type="radio"
											id="credit-based"
											name="billing-model"
											checked={billingModel === 'credit_based'}
											onChange={() => switchBillingModel('credit_based')}
											className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
										/>
										<label
											htmlFor="credit-based"
											className="text-sm font-medium text-gray-700"
										>
											Credit-Based
										</label>
									</div>
									<div className="flex items-center space-x-2">
										<input
											type="radio"
											id="metered-usage"
											name="billing-model"
											checked={billingModel === 'metered_usage'}
											onChange={() => switchBillingModel('metered_usage')}
											className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
										/>
										<label
											htmlFor="metered-usage"
											className="text-sm font-medium text-gray-700"
										>
											Metered Usage
										</label>
									</div>
								</div>
							</div>

							{/* Billing Model Description */}
							<div className="mt-4 p-4 bg-neutral-50 rounded-lg">
								{billingModel === 'credit_based' ? (
									<div className="text-sm text-neutral-700 font-primary">
										<strong>Credit-Based Billing:</strong> Purchase credits
										upfront and use them for statement analysis. Each statement
										analysis costs 1 credit. Credits never expire.
									</div>
								) : (
									<div className="text-sm text-neutral-700 font-primary">
										<strong>Metered Usage Billing:</strong> Pay only for what
										you use. You'll be billed monthly based on the number of
										statements processed. No upfront payment required.
									</div>
								)}
							</div>
						</CardBody>
					</Card>
				)}

				{/* Main Card */}
				<Card className="w-full p-7">
					<CardBody className="flex justify-center items-stretch gap-10 min-h-[200px]">
						{/* Left side: Credits remaining */}
						<div className="flex-1 bg-sand-50 rounded-2xl flex flex-col items-center justify-center gap-2.5">
							<div className="w-16 h-16 relative flex items-center justify-center">
								<Files className="w-11 h-12 text-neutral-800" />
							</div>
							<div className="text-center text-black text-lg font-medium leading-tight mt-2.5 font-primary">
								You have{' '}
								{subscription.creditsRemaining === -1
									? 0
									: subscription.creditsRemaining}{' '}
								reports left
							</div>
						</div>

						{/* Right side: Purchase options */}
						<div className="flex flex-col justify-start items-start gap-2.5">
							{billingModel === 'credit_based' ? (
								<>
									{/* Header section */}
									<div className="w-full pb-5 flex flex-col justify-start items-center gap-2.5">
										<div className="text-black text-xl leading-normal font-primary">
											Add more reports
										</div>
										<div className="flex items-baseline">
											<span className="text-black text-4xl font-medium font-primary">
												$40
											</span>
											<span className="text-neutral-400 text-lg font-normal leading-tight font-primary">
												/statement report
											</span>
										</div>
										<div className="w-full text-center text-neutral-400 text-sm font-normal font-primary">
											Each report gives you a clear breakdown of your fees with
											advanced insights.
										</div>
									</div>

									{/* Form section */}
									<div className="w-full pt-7 border-t border-neutral-200 flex flex-col justify-start items-start gap-5">
										<div className="w-full flex flex-col justify-start items-start gap-2.5">
											<div className="w-full text-black text-sm font-medium font-primary">
												Number of reports to subscribe
											</div>
											<Input
												type="text"
												value={quantity}
												onChange={(e) => {
													const value = e.target.value;
													if (value === '' || /^\d+$/.test(value)) {
														setQuantity(value);
													}
												}}
												placeholder="Enter the number"
												variant="outlined"
												inputSize="md"
												className="text-black text-base font-normal placeholder:text-black placeholder:opacity-50"
											/>
											<Button
												variant="text"
												onClick={() => setShowPromoCode(!showPromoCode)}
												className="text-primary-600 text-sm font-medium"
											>
												I have a promo code
											</Button>

											{/* Promo Code Input */}
											{showPromoCode && (
												<div className="w-full flex flex-col items-center gap-2 mt-2">
													<Input
														type="text"
														value={promoCode}
														onChange={(e) =>
															setPromoCode(e.target.value.toUpperCase())
														}
														placeholder="Enter promo code"
														variant="outlined"
														inputSize="md"
														rightButton={
															appliedPromoCode
																? 'Clear'
																: validatingPromo
																	? '...'
																	: 'Apply'
														}
														onRightButtonClick={
															appliedPromoCode
																? clearPromoCode
																: validatePromoCode
														}
														rightButtonDisabled={
															validatingPromo ||
															(!appliedPromoCode && !promoCode.trim())
														}
														className="flex-1"
														disabled={validatingPromo || !!appliedPromoCode}
													/>
													{promoValidation && (
														<div
															className={`w-full mt-2 text-sm font-medium ${promoValidation.valid ? 'text-green-600' : 'text-red-600'}`}
														>
															{promoValidation.message}
														</div>
													)}
												</div>
											)}
										</div>
										<Button
											onClick={handlePurchaseCredits}
											disabled={
												processingPayment || !quantity || parseInt(quantity) < 1
											}
											variant="primary"
											size="md"
											fullWidth
											loading={processingPayment}
										>
											Purchase Reports
										</Button>
									</div>
								</>
							) : (
								<>
									{/* Metered Billing Header */}
									<div className="w-full pb-5 flex flex-col justify-start items-center gap-2.5">
										<div className="text-black text-xl font-medium leading-snug">
											Metered Usage Billing
										</div>
										<div className="flex items-baseline">
											<span className="text-black text-3xl font-medium leading-9">
												$40
											</span>
											<span className="text-neutral-400 text-lg font-normal leading-tight">
												/statement
											</span>
										</div>
										<div className="w-full text-center text-neutral-400 text-sm font-normal">
											Pay only for what you use. No upfront payment required.
										</div>
									</div>

									{/* Metered Form section */}
									<div className="w-full pt-7 border-t border-neutral-200 flex flex-col justify-start items-start gap-5">
										<div className="w-full flex flex-col justify-start items-start gap-2.5">
											<button
												onClick={() => setShowPromoCode(!showPromoCode)}
												className="text-indigo-700 text-sm font-medium hover:underline"
											>
												I have a promo code
											</button>

											{/* Promo Code Input for Metered */}
											{showPromoCode && (
												<div className="w-full flex items-center gap-2 mt-2">
													<Input
														type="text"
														value={promoCode}
														onChange={(e) =>
															setPromoCode(e.target.value.toUpperCase())
														}
														placeholder="Enter promo code"
														variant="outlined"
														inputSize="md"
														rightButton={
															appliedPromoCode
																? 'Clear'
																: validatingPromo
																	? '...'
																	: 'Apply'
														}
														onRightButtonClick={
															appliedPromoCode
																? clearPromoCode
																: validatePromoCode
														}
														rightButtonDisabled={
															validatingPromo ||
															(!appliedPromoCode && !promoCode.trim())
														}
														className="flex-1"
														disabled={validatingPromo || !!appliedPromoCode}
													/>
													{promoValidation && (
														<div
															className={`w-full mt-2 text-sm font-medium ${promoValidation.valid ? 'text-green-600' : 'text-red-600'}`}
														>
															{promoValidation.message}
														</div>
													)}
												</div>
											)}
										</div>
										<Button
											onClick={handleSetupMetered}
											disabled={processingPayment}
											variant="primary"
											size="md"
											fullWidth
											loading={processingPayment}
										>
											Setup Metered Billing
										</Button>
									</div>
								</>
							)}
						</div>
					</CardBody>
				</Card>

				{/* Invoices Section */}
				<Card className="w-full flex-1">
					<CardBody className="p-0">
						{/* Header */}
						<div className="w-full py-5 flex justify-between items-center gap-2.5 border-b border-neutral-200">
							<div className="flex-1 text-black text-lg font-medium leading-tight">
								Invoices
							</div>
							<div className="w-96">
								<SearchInput
									className="w-[200px]"
									placeholder="Search invoices..."
									value={searchTerm}
									onChange={setSearchTerm}
								/>
							</div>
						</div>

						{/* Table */}
						<div className="w-full flex-1 flex flex-col justify-start items-start">
							{/* Table Header */}
							<div className="w-full h-12 px-5 bg-gray-100 flex justify-start items-center gap-2.5 border-b border-gray-200">
								<div className="flex-1 text-neutral-600 text-xs font-medium uppercase tracking-wide">
									Date
								</div>
								<div className="flex-1 text-neutral-600 text-xs font-medium uppercase tracking-wide">
									Quantity
								</div>
								<div className="flex-1 text-neutral-600 text-xs font-medium uppercase tracking-wide">
									Amount
								</div>
								<div className="flex-1 text-neutral-600 text-xs font-medium uppercase tracking-wide">
									Status
								</div>
							</div>

							{/* Table Body */}
							{filteredInvoices.length === 0 ? (
								<div className="w-full py-12 px-5 text-center">
									<div className="text-neutral-600 text-sm font-normal">
										{searchTerm
											? 'No invoices found matching your search.'
											: 'No invoices found.'}
									</div>
								</div>
							) : (
								filteredInvoices.map((invoice: Invoice, index: number) => (
									<div
										key={invoice.id}
										className={`w-full px-5 py-4 ${filteredInvoices.length - 1 !== index ? 'border-b' : 'border-b-0'} border-neutral-200 flex justify-start items-center gap-2.5 hover:bg-gray-50 cursor-pointer transition-colors`}
										onClick={() => fetchInvoiceDetails(invoice.id.toString())}
									>
										<div className="flex-1 flex justify-between items-center">
											<div className="flex-1 text-zinc-800 text-sm font-medium">
												{new Date(invoice.created * 1000).toLocaleDateString(
													'en-US',
													{
														month: 'short',
														day: '2-digit',
														year: 'numeric',
													},
												)}
											</div>
										</div>
										<div className="flex-1 flex justify-between items-center">
											<div className="flex-1 text-neutral-600 text-sm font-normal">
												{invoice.lines?.[0]?.quantity || 0}
											</div>
										</div>
										<div className="flex-1 flex justify-between items-center">
											<div className="flex-1 text-neutral-600 text-sm font-normal">
												{formatCurrency(invoice.amount_paid)}
											</div>
										</div>
										<div className="flex-1 flex justify-between items-center">
											<StatusPill
												status={
													invoice.status === 'paid'
														? 'completed'
														: invoice.status === 'pending'
															? 'processing'
															: 'failed'
												}
											/>
										</div>
									</div>
								))
							)}
						</div>
					</CardBody>
				</Card>

				{/* Invoice Details Modal */}
				{showInvoiceModal && selectedInvoice && (
					<div className="fixed inset-0 bg-neutral-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
						<div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
							<div className="mt-3">
								<div className="flex justify-between items-center mb-4">
									<h3 className="text-lg font-semibold text-gray-900">
										Invoice Details
									</h3>
									<button
										onClick={() => setShowInvoiceModal(false)}
										className="text-gray-400 hover:text-gray-600"
									>
										<svg
											className="w-6 h-6"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M6 18L18 6M6 6l12 12"
											/>
										</svg>
									</button>
								</div>

								<div className="space-y-4">
									<div className="grid grid-cols-2 gap-4">
										<div>
											<p className="text-sm font-medium text-gray-500">
												Invoice ID
											</p>
											<p className="text-sm text-gray-900">
												{selectedInvoice?.id || 'N/A'}
											</p>
										</div>
										<div>
											<p className="text-sm font-medium text-gray-500">Date</p>
											<p className="text-sm text-gray-900">
												{selectedInvoice
													? formatDate(
															new Date(
																selectedInvoice.created * 1000,
															).toISOString(),
														)
													: 'N/A'}
											</p>
										</div>
										<div>
											<p className="text-sm font-medium text-gray-500">
												Status
											</p>
											<StatusPill
												status={
													selectedInvoice?.status === 'paid'
														? 'completed'
														: selectedInvoice?.status === 'pending'
															? 'processing'
															: 'failed'
												}
											>
												{selectedInvoice?.status || 'Unknown'}
											</StatusPill>
										</div>
										<div>
											<p className="text-sm font-medium text-gray-500">
												Quantity
											</p>
											<p className="text-sm text-gray-900">
												{selectedInvoice?.lines?.[0]?.quantity || 0}
											</p>
										</div>
									</div>

									<div className="border-t pt-4">
										<div className="space-y-2">
											<div className="flex justify-between">
												<span className="text-sm text-gray-600">Subtotal:</span>
												<span className="text-sm text-gray-900">
													{selectedInvoice
														? formatCurrency(selectedInvoice.subtotal)
														: '$0.00'}
												</span>
											</div>
											<div className="flex justify-between font-semibold">
												<span className="text-sm">Total:</span>
												<span className="text-sm">
													{selectedInvoice
														? formatCurrency(selectedInvoice.total)
														: '$0.00'}
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
			</PageContent>
		</div>
	);
};

export default SubscriptionPage;
